import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

const GUEST_CV_KEY = 'cvly-guest-cv-created';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/user', { credentials: 'include' });
      if (!res.ok) {
        setUser(null);
        return;
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setUser(null);
        return;
      }
      const data = await res.json();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(() => {
    window.location.href = '/api/login';
  }, []);

  const logout = useCallback(() => {
    window.location.href = '/api/logout';
  }, []);

  const isAuthenticated = !!user;

  const hasGuestCVCreated = useCallback(() => {
    return localStorage.getItem(GUEST_CV_KEY) === 'true';
  }, []);

  const markGuestCVCreated = useCallback(() => {
    localStorage.setItem(GUEST_CV_KEY, 'true');
  }, []);

  const canCreateCV = useCallback(() => {
    if (isAuthenticated) return true;
    return !hasGuestCVCreated();
  }, [isAuthenticated, hasGuestCVCreated]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        logout,
        hasGuestCVCreated,
        markGuestCVCreated,
        canCreateCV,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
